// Disable max-lines rule for this file, until the code moves into
// custom creative packages and the plugin is reduced or removed.
/* eslint-disable max-lines */
import type { AdNodeManager } from "../../core/AdNodeManager";
import type { GptWrapper } from "../../core/GptWrapper";
import type { TopStrategyParams } from "./TopStrategyTuner.types.ts";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";

// TODO: Remove in COTECH-2882
import { TopMarginApplier } from "./features/helpers/TopMarginApplier";
import {
  DEFAULT_TOP_SLOT_NAME,
  FAN_TAKEOVER_SIZES,
  TENTPOLE_ACTIVATOR_TIMEOUT,
} from "../../helpers/takeoverConsts";
import { isDefaultTopSlot } from "./helpers/isDefaultTopSlot";
import { isFanTakeoverSize } from "./helpers/isFanTakeoverSize";
import { isNotForDefaultTopSlotOrEmpty } from "./helpers/isNotForDefaultTopSlotOrEmpty";
import { applyStickyTopBoxadIfFandomDesktop } from "./features/helpers/applyStickyTopBoxadIfFandomDesktop";

import { SlotRefresherPlugin } from "../SlotRefresherPlugin/SlotRefresherPlugin";
import { InContentPlayerPlugin } from "../InContentPlayerPlugin/InContentPlayerPlugin";
import { hasFeaturedVideoOnPage } from "../../helpers/hasVideoOnPage";
import { isMobile } from "../../helpers/isMobile";
import { Comparator } from "../../helpers/slotSizes";
import { VastUrlPlugin } from "../VastUrlPlugin/VastUrlPlugin";

import { StickyFeatureActivator } from "./activators/StickyFeatureActivator";
import { SkyboxFeatureActivator } from "./activators/SkyboxFeatureActivator";
import { SlotManagerFeatureActivator } from "./activators/SlotManagerFeatureActivator";
import { VideoBannerFeatureActivator } from "./activators/VideoBannerFeatureActivator";
import { waitWithTimeout } from "utils";
import { getApp } from "../../helpers/getApp";
import { FloorAdhesionPlugin } from "../FloorAdhesionPlugin/FloorAdhesionPlugin";
import { retrieveUapKeyValue } from "./helpers/retrieveUapKeyValue";
import { forceRepaintOnSafari } from "./helpers/forceRepaintOnSafari";

export interface TopStrategyTunerPluginApi {
  activate: (options: TopStrategyParams) => Promise<void>;
  onRemoveStickiness: (callback: () => void) => void;
  onSkyboxStateResolved: (callback: () => void) => void;
  onTlbLoaded: (callback: (isSkyboxLoaded: boolean) => void) => void;
}

type FeaturesFlags = {
  skyboxResolved: boolean;
};

/**
 * # TopStrategyTunerPlugin
 *
 * Goal for this plugin is to provide takeover functionality, with GAM creatives that would call this api function 'activate'.
 * For more details, how to use, go and see doc file:
 * @see /docs/plugins/TopStrategyTunerPlugin.md
 */
export class TopStrategyTunerPlugin extends AdEnginePlugin<TopStrategyTunerPluginApi> {
  private adNodeManager: AdNodeManager | undefined;
  private gpt: GptWrapper | undefined;
  private defaultStickyTlbPromiseResolver: () => void = () => {};
  private defaultStickyTlbPromiseRejecter: () => void = () => {};

  private readonly featuresFlags: FeaturesFlags = {
    skyboxResolved: false,
  };

  private defaultStickyTlbApplied = false;
  private stickyActivator?: StickyFeatureActivator;
  private skyboxActivator?: SkyboxFeatureActivator;
  private slotManagerActivator?: SlotManagerFeatureActivator;
  private videoFeatureActivator?: VideoBannerFeatureActivator;
  private readonly tentpoleActivator = Promise.withResolvers<void>();

  private readonly onTlbLoadedCallbacks: ((isSkyboxLoaded: boolean) => void)[] =
    [];

  // This flag informs us if a Fan Takeover ad was loaded on the page; initially it's set to null and only after top ad rendered it becomes a boolean
  private hasFanTakeoverLoaded: boolean | null = null;

  constructor(
    private readonly config: { icForceStickyTlb?: boolean } = {},
    // TODO: Remove in COTECH-2882
    private readonly marginApplier: TopMarginApplier = new TopMarginApplier(),
    private readonly defaultStickyTlbPromise?: Promise<void>,
  ) {
    super();

    this.defaultStickyTlbPromise ??= new Promise<void>((resolve, reject) => {
      this.defaultStickyTlbPromiseResolver = resolve;
      this.defaultStickyTlbPromiseRejecter = reject;
    });

    document.body.classList.add("odyssey", "odyssey-top-strategy");
  }

  private activateStickyFeature(options: TopStrategyParams): void {
    if (!options.sticky && this.defaultStickyTlbApplied) return;

    // TODO: Remove `this.marginApplier` in COTECH-2882
    this.stickyActivator ??= new StickyFeatureActivator(this.marginApplier);
    this.stickyActivator.activate(options);
  }

  private activateSkyboxFeature(options: TopStrategyParams): void {
    if (!options.skybox) return;

    // TODO: Remove `this.marginApplier` in COTECH-2882
    this.skyboxActivator ??= new SkyboxFeatureActivator(
      this,
      this.marginApplier,
    );

    this.stickyActivator?.deactivate();
    this.skyboxActivator.activate(options, () => {
      this.featuresFlags.skyboxResolved = true;
      this.stickyActivator?.reactivate(options, true);
      forceRepaintOnSafari();
    });

    this.onTlbLoadedCallbacks.forEach((callback) => callback(true));
  }

  private activateSlotManagerFeature(options: TopStrategyParams): void {
    if (!options.takeover || !options.enabledSlots?.length) return;

    this.slotManagerActivator = new SlotManagerFeatureActivator(
      this.adNodeManager!,
    );
    this.slotManagerActivator.activate({ enabledSlots: options.enabledSlots });
  }

  // TODO: Address complexity later, perhaps by wrapping logic with new TentpoleActivator/TentpoleFeature classes

  private activateTentpoleFeature(options: TopStrategyParams): void {
    if (typeof options.tentpole !== "object") {
      // This will let activateOrUnblockProperPluginOnDefaultTopSlotRender to unblock the video
      this.tentpoleActivator.resolve();
      return;
    }

    const { tentpole } = options;

    if (this.gpt) {
      this.gpt.setTargeting("uap", retrieveUapKeyValue(tentpole?.lineItemId));
      this.gpt.setTargeting("uap_c", retrieveUapKeyValue(tentpole?.creativeId));
    }

    this.usePlugin(SlotRefresherPlugin)?.stop();
    this.usePlugin(VastUrlPlugin)?.unblockVideo();

    // Tell the floor adhesion plugin to activate tentpole so on video close, it will
    // unblock and be able to render regular ads in the floor adhesion slot
    this.usePlugin(FloorAdhesionPlugin)?.activateTentpole();

    applyStickyTopBoxadIfFandomDesktop(getApp(), this);
  }

  /**
   * This method attaches slotRenderEnded event on which we handle different scenarios of our business logic.
   *
   * The logic is related to the top ad slot on our pages (@see {@link DEFAULT_TOP_SLOT_NAME})
   * and based on the GAM response, config and device the method communicate with other plugins (ie. FloorAdhesionPlugin),
   * activates features (such as sticky feature), if the top ad slot loaded Fan Takeover ad updates @see hasFanTakeoverLoaded flag.
   *
   * @param gptWrapper
   * @private
   */
  private activateOrUnblockProperPluginOnDefaultTopSlotRender(
    gptWrapper: GptWrapper,
  ): void {
    gptWrapper.addEventListener(
      "slotRenderEnded",
      async (event: googletag.events.SlotRenderEndedEvent) => {
        this.handleUnblockVideoOnDefaultSlotRender(event);

        if (
          !isMobile() &&
          !this.config.icForceStickyTlb &&
          isNotForDefaultTopSlotOrEmpty(event)
        ) {
          return;
        }

        if (!isMobile() && isDefaultTopSlot(event)) {
          this.defaultStickyTlbPromise?.then(() => {
            // by default on desktop we make the top leaderboard sticky when icForceStickyTlb=true
            this.defaultStickyTlbApplied = true;
            this.activateStickyFeature({
              id: "unknown",
              slotName: DEFAULT_TOP_SLOT_NAME,
              sticky: true,
            });
          });
          setTimeout(() => {
            this.defaultStickyTlbPromiseResolver();
          }, 100); // Wait for the sticky feature to be activated
        }

        this.handleInContentVideo(event);
      },
    );
  }

  private handleDefaultTopLeaderboardFirstImpression(
    gptWrapper: GptWrapper,
  ): void {
    gptWrapper.addEventListener("impressionViewable", (event) => {
      if (this.skyboxActivator) {
        return;
      }

      const renderCount = this.adNodeManager
        ?.getAdNodeByName(DEFAULT_TOP_SLOT_NAME)
        ?.getRenderCount();

      if (
        event.slot.getSlotElementId() === DEFAULT_TOP_SLOT_NAME &&
        renderCount === 1
      ) {
        this.onTlbLoadedCallbacks.forEach((callback) => callback(false));
      }
    });
  }

  private handleUnblockVideoOnDefaultSlotRender(
    event: googletag.events.SlotRenderEndedEvent,
  ): void {
    if (!isDefaultTopSlot(event)) {
      return;
    }
    const unblock = () => {
      this.usePlugin(VastUrlPlugin)?.unblockVideo();
    };

    // TODO: adjust the waiting time or think about using AdEngine.waitForFirstRenderCycleComplete() instead
    // Give UAP Custom Creative a TENTPOLE_ACTIVATOR_TIMEOUT window to unblock the video, otherwise just do it
    waitWithTimeout(
      this.tentpoleActivator.promise,
      TENTPOLE_ACTIVATOR_TIMEOUT,
    ).then(unblock, unblock); // unblock in both resolved and rejected cases
  }

  private handleInContentVideo(
    event: googletag.events.SlotRenderEndedEvent,
  ): void {
    if (isDefaultTopSlot(event)) {
      this.hasFanTakeoverLoaded = isFanTakeoverSize(event);

      if (!this.hasFanTakeoverLoaded && !hasFeaturedVideoOnPage()) {
        const inContentPlugin = this.usePlugin(InContentPlayerPlugin);
        inContentPlugin?.initOnce();
      }
    }
  }

  private activateVideoFeature(options: TopStrategyParams): void {
    if (!options.videoUrl) return;

    this.videoFeatureActivator ??= new VideoBannerFeatureActivator();
    this.videoFeatureActivator.activate(options, () => {
      this.skyboxActivator?.moveToResolvedState(options);
    });
    this.stickyActivator?.addOnRemoveStickiness(() => {
      this.videoFeatureActivator?.stopVideoOnOutOfViewport();
    });
  }

  api: TopStrategyTunerPluginApi = {
    activate: async (options: TopStrategyParams): Promise<void> => {
      options.slotName ??= DEFAULT_TOP_SLOT_NAME;
      this.defaultStickyTlbPromiseRejecter();

      if (options.skybox) {
        this.usePlugin(SlotRefresherPlugin)?.stop();
      }

      this.activateTentpoleFeature(options);
      this.activateStickyFeature(options);
      this.activateSlotManagerFeature(options);
      this.activateSkyboxFeature(options);
      this.activateVideoFeature(options);
    },
    onRemoveStickiness: (callback: () => void) => {
      this.stickyActivator?.addOnRemoveStickiness(callback);
    },
    /**
     * Register a callback to be notified when the skybox state is resolved. If the skybox
     * feature is not active, then this function will do nothing.
     *
     * @param callback function to call when the skybox state is resolved.
     */
    onSkyboxStateResolved: (callback: () => void) => {
      this.skyboxActivator?.onSkyboxStateResolved(callback);
    },
    onTlbLoaded: (callback: (isSkyboxLoaded: boolean) => void) => {
      this.onTlbLoadedCallbacks.push(callback);
    },
  };

  hooks: Hooks = {
    onAfterAdNodeManagerInit: (adNodeManager: AdNodeManager) => {
      this.adNodeManager = adNodeManager;

      if (hasFeaturedVideoOnPage()) {
        this.adNodeManager
          .getAdNodeByName(DEFAULT_TOP_SLOT_NAME)
          ?.removeSizes(FAN_TAKEOVER_SIZES.MAX, Comparator.LESS_THAN_OR_EQUAL);
      }
    },
    onAfterGptWrapperInit: (gpt: GptWrapper) => {
      this.gpt = gpt;
      this.activateOrUnblockProperPluginOnDefaultTopSlotRender(gpt);
      this.handleDefaultTopLeaderboardFirstImpression(gpt);
    },
  };
}
